import { Utils } from 'app/core/utils';
import { TrivialError } from 'core/error/error';
import split from 'lodash/split';
import { CharDimensions, Text, TextChunk } from './text.model';


export class InvalidCharConfigError extends TrivialError
{
  // i18n.error.invalidCharConfigError.title
  // i18n.error.invalidCharConfigError.message
  protected _name: string = 'InvalidCharConfigError';
}

export enum TextEditingRefusalReason {
  None,
  TextTooHigh,
  TextTooWide,
  CharacterNotSupported,
  AreaTooSmall,
  MinFontSizeUndershot,
}

export interface ProblemPosition
{
  chunk: number;
  character: number;
}

export namespace TextControllerMethods
{
  interface Dimensions
  {
    width: number;
    height: number;
  }

  interface FittingResult
  {
    finalResult: TextEditingRefusalReason;
    fixedResult: TextEditingRefusalReason;
  }

  export function reduceFontSizeChecked(
      model: Text, minSize: number): FittingResult
  {
    let result = TextEditingRefusalReason.None;
    let fixed = TextEditingRefusalReason.None;

    while ((result = getUsageAvailableAreaComparison(model)) !==
           TextEditingRefusalReason.None) {
      if (Math.floor(model.fontSize) > minSize) {
        model.fontSize --;
        fixed = result;
      }
      else {
        break;
      }
    }

    return {
      finalResult: result,
      fixedResult: fixed,
    };
  }

  export function reduceFontSize(
    model: Text, minSize: number): TextEditingRefusalReason
  {
    return reduceFontSizeChecked(model, minSize).finalResult;
  }

  export function increaseFontSize(model: Text): TextEditingRefusalReason
  {
    const initialArea = getTextUsageArea(model);
    if (! (initialArea.width > 0 && initialArea.height > 0)) {
      return TextEditingRefusalReason.None;
    }

    let result = TextEditingRefusalReason.None;
    while ((result = getUsageAvailableAreaComparison(model)) ===
            TextEditingRefusalReason.None) {
      model.fontSize ++;
    }
    if (result === TextEditingRefusalReason.TextTooHigh ||
        result === TextEditingRefusalReason.TextTooWide) {
      model.fontSize --;
      result = TextEditingRefusalReason.None;
    }

    return result;
  }

  function compareUsageAvailableArea(model: Text, usageArea: Dimensions):
    TextEditingRefusalReason
  {
    return usageArea.width > model.availableArea.width
        ? TextEditingRefusalReason.TextTooWide
        : (usageArea.height > model.availableArea.height
            ? TextEditingRefusalReason.TextTooHigh
            : TextEditingRefusalReason.None);
  }

  export function getUsageAvailableAreaComparison(model: Text):
      TextEditingRefusalReason
  {
    return compareUsageAvailableArea(model, getTextUsageArea(model));
  }

  // the function's result is in pt (or something like that)
  export function getTextUsageArea(model: Text): Dimensions
  {
    const linesDimensions = getLinesDimensions(model);

    let width = 0;
    let height = 0;
    for (const lineDimensions of linesDimensions) {
      width = Math.max(width, lineDimensions.width);
      height = height + lineDimensions.height;
    }

    return {
        'width': width,
        'height': height,
    };
  }

  // the function's result is in pt (or something like that)
  function getLinesDimensions(model: Text): Array<Dimensions>
  {
    const bearing = model.fontDescription.charDimensions.maxBearing;
    const baseEM = model.fontDescription.charDimensions.baseEM;
    let linesDimensions = [];

    for (const chunks of getChunksAsLines(model)) {
      // bearing is negative per default but significant for width calculation
      let _width = -1 * (bearing.left + bearing.right);
      for (const chunk of chunks) {
        _width = _width +
            calculateStringWidth(chunk.content,
                model.fontDescription.charDimensions);
      }
      linesDimensions.push({
        width: _width * model.fontSize / baseEM,
        height: model.leading * model.fontSize
      });
    }

    return linesDimensions;
  }

  function getChunksAsLines(model): Array<Array<TextChunk>>
  {
    let chunkLines = [];
    let chunkLine = [];
    for (const chunk of model.text) {
      if (chunk.breaksLine) {
        chunkLines.push(chunkLine);
        chunkLine = [];
      }
      chunkLine.push(chunk);
    }
    if (chunkLine.length > 0) {
      chunkLines.push(chunkLine);
    }
    return chunkLines;
  }

  // the function's result is in magical (or none) units
  export function calculateStringWidth(
      text: string | undefined, charDimensions: CharDimensions): number
  {
    if (! text) {
      return 0;
    }

    let width = 0;
    const textAsIntArray = getTextAsIntArray(text);

    const getKerningWidth = (idx: number) => {
      const kernings = charDimensions.widths[textAsIntArray[idx]].kerning;
      const nextCodePoint = textAsIntArray[idx + 1];
      return kernings && nextCodePoint && kernings[nextCodePoint]
        ? kernings[nextCodePoint] : 0;
    };

    for (let idx = 0; idx < textAsIntArray.length; idx++) {
      const codePoint = textAsIntArray[idx];
      const glyphDimensions = charDimensions.widths[codePoint];
      const glyphWidth = glyphDimensions
          ? (glyphDimensions.width + getKerningWidth(idx))
          : charDimensions.baseEM;
      width = width + glyphWidth;
    }

    if (width < 0) {
      throw new InvalidCharConfigError('invalid config for: ' + text);
    }

    return width;
  }

  function getTextAsIntArray(text: string): Array<number>
  {
    // str.split() doesn't support Glyphs with multiple unicode characters
    return (Array.from([...<any>text])
      .map(c => split(c, '')) as any)
      .flat()
      .map(x => x.codePointAt(0));
  }

  export function checkForInvalidSymbols(model: Text):
      TextEditingRefusalReason | ProblemPosition
  {
    const unicodeRanges = model.fontDescription.unicodeRanges;

    const faultyChunks = (
      Utils
        .zip(Utils.range(0, model.text.length), model.text)
        .filter(it => !!(it[1].content))
        .filter(it => !textIsCoveredByUnicodeRanges(it[1].content,
                                                    unicodeRanges)));

    if (faultyChunks.length > 0) {
      return {
        chunk: faultyChunks[0][0],
        character: 0
      };
    } else {
      return TextEditingRefusalReason.None;
    }
  }

  export function textIsCoveredByUnicodeRanges(
      text: string|undefined, unicodeRanges: Array<Array<number>>): boolean
  {
    if (!text) {
      return true;
    }

    const isInRange = (charPoint: number)  => {
      for (let range of unicodeRanges) {
        const inRange = (range.length === 1
            ? range[0] === charPoint
            : (charPoint >= range[0] && charPoint <= range[1]));
        if (inRange) {
          return true;
        }
      }

      return false;
    };

    const textAsIntArray = getTextAsIntArray(text);
    for (let idx = 0; idx < textAsIntArray.length; idx++) {
      if (!isInRange(textAsIntArray[idx])) {
        return false;
      }
    }
    return true;
  }

  export function locateProblemPosition(
      model: Text, type: TextEditingRefusalReason): ProblemPosition
  {
    let chunkIdx = -1;
    const lineDimensions = getLinesDimensions(model);
    const chunkLines = getChunksAsLines(model);

    const getProblemChunk = (lineIdx, chunkIdxOfLine) => {
      let _chunkIdx = 0;
      for (let i = 0; i < lineIdx; i++) {
        _chunkIdx = _chunkIdx + chunkLines[i].length;
      }
      return _chunkIdx + chunkIdxOfLine;
    };
    const _inPt = (width) => width * model.fontSize /
        model.fontDescription.charDimensions.baseEM;

    switch (type) {
      case TextEditingRefusalReason.TextTooHigh: {
        const problemLineIdx = chunkLines.length - 1;
        return {
          chunk: getProblemChunk(problemLineIdx, 0),
          character: 0
        };
      }

      case TextEditingRefusalReason.TextTooWide: {
        const charDimensions = model.fontDescription.charDimensions;

        let problemLineIdx = 0;
        for (let i = 0; i < lineDimensions.length; i++) {
          if (lineDimensions[i].width > model.availableArea.width) {
            problemLineIdx = i;
            break;
          }
        }

        let width = 0;
        let _chunk = undefined;
        for (let i = 0; i < chunkLines[problemLineIdx].length; i++) {
          _chunk = chunkLines[problemLineIdx][i];
          const _width =
              _inPt(calculateStringWidth(_chunk.content, charDimensions));
          width = width + _width;
          if (width > model.availableArea.width) {
            chunkIdx = getProblemChunk(problemLineIdx, i);
            width = width - _width;
            break;
          }
        }

        for (let i = 0; i < (_chunk.content || '').length; i++) {
          width = width +
              _inPt(calculateStringWidth(_chunk.content[i], charDimensions));
          if (width > model.availableArea.width) {
            return {
              chunk: chunkIdx,
              character: i
            };
          }
        }
        break;
      }

      case TextEditingRefusalReason.AreaTooSmall: {
        let chunkLineIdx = -1;
        let maxWidth = 0;
        for (let lineIdx of Object.keys(lineDimensions)) {
          const dimensions = lineDimensions[lineIdx];
          if (dimensions.width > maxWidth) {
            maxWidth = dimensions.width;
            chunkLineIdx = Number(lineIdx);
          }
        }

        chunkIdx = getProblemChunk(
            chunkLineIdx, chunkLines[chunkLineIdx].length - 1);
        return {
          chunk: chunkIdx,
          character: model.text[chunkIdx].content.length - 1
        };
      }

      default: /* nothing to see here*/
    }
  }

  export function removeVariationSelectors(text: Array<TextChunk>):
      Array<TextChunk>
  {
    // Variation Selectors:
    // \u180B-\u180D: Mongolian free variation selectors
    // \uFE00-\uFE0F: Unicode variation selectors
    // \uDB40[\uDD00-\uDDEF]: Unicode CJK Ideographic variation selectors
    // \uD83C[\uDFFB-\uDFFF]: Emoji modifier characters for skin tone
    const regex = new RegExp('(' +
        '[\u180B-\u180D\uFE00-\uFE0F]|' +
        '\uDB40[\uDD00-\uDDEF]|' +
        '\uD83C[\uDFFB-\uDFFF])', 'g');

    return text.map(x => {
      if (x.content) {
        x.content = x.content.replace(regex, '');
      }
      return x;
    });
  }
}
